import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { apiService } from "@/api/api";
import Navbar from "@/components/layout/Navbar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Search, Calendar, User } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

interface Session {
  session_id: string;
  session_title?: string;
  session_type?: string;
  session_timestamp: string;
  confirmed?: boolean;
  session_status: string;
  durationMinutes?: number;
  notes?: string;
  timestamp?: number;
  caregiver_id?: string;
  client_id?: string;
  clientName?: string; // This will be populated after fetching
  transcription?: any[];
  summary?: string;
  lastUpdated?: string;
  template_type?: string;
  status?: string;
  clientLoading?: boolean; // Track loading state for individual clients
}

// Cache for client data to avoid duplicate API calls
const clientCache = new Map<string, { first_name: string; last_name: string }>();

const Sessions = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [sessions, setSessions] = useState<Session[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  // Fetch client data for a session
  const fetchClientData = useCallback(async (session: Session) => {
    if (!session.client_id || clientCache.has(session.client_id)) {
      return;
    }

    try {
      // Mark this session's client as loading
      setSessions(prev => prev.map(s => 
        s.session_id === session.session_id 
          ? { ...s, clientLoading: true }
          : s
      ));

      const clientData = await apiService.getClient(session.client_id);
      if (clientData?.data) {
        const clientInfo = {
          first_name: clientData.data.first_name,
          last_name: clientData.data.last_name
        };
        
        // Cache the client data
        clientCache.set(session.client_id, clientInfo);
        
        // Update the session with client name
        setSessions(prev => prev.map(s => 
          s.client_id === session.client_id
            ? { 
                ...s, 
                clientName: `${clientInfo.first_name} ${clientInfo.last_name}`,
                clientLoading: false
              }
            : s
        ));
      }
    } catch (error) {
      console.error(`Could not fetch client details for ${session.client_id}:`, error);
      // Mark as failed but don't block the UI
      setSessions(prev => prev.map(s => 
        s.session_id === session.session_id 
          ? { ...s, clientLoading: false }
          : s
      ));
    }
  }, []);

  // Batch fetch client data with concurrency control
  const fetchClientDataBatch = useCallback(async (sessions: Session[]) => {
    const BATCH_SIZE = 5; // Limit concurrent requests
    const sessionsNeedingClientData = sessions.filter(s => 
      s.client_id && !clientCache.has(s.client_id) && !s.clientName
    );

    for (let i = 0; i < sessionsNeedingClientData.length; i += BATCH_SIZE) {
      const batch = sessionsNeedingClientData.slice(i, i + BATCH_SIZE);
      await Promise.allSettled(batch.map(fetchClientData));
      
      // Small delay to prevent overwhelming the API
      if (i + BATCH_SIZE < sessionsNeedingClientData.length) {
        await new Promise(resolve => setTimeout(resolve, 100));
      }
    }
  }, [fetchClientData]);

  // Sort sessions by timestamp (most recent first) - memoized for performance
  const sortedSessions = useMemo(() => {
    const normalize = (ts: string): string => {
      return ts.replace(/(\.\d{3})\d+/, '$1');
    };

    return [...sessions].sort((a, b) => {
      const dateA = new Date(normalize(a.session_timestamp)).getTime();
      const dateB = new Date(normalize(b.session_timestamp)).getTime();
      
      // Handle invalid dates gracefully (put them at the bottom)
      if (isNaN(dateA) && isNaN(dateB)) return 0;
      if (isNaN(dateA)) return 1;
      if (isNaN(dateB)) return -1;
      
      return dateB - dateA; // Most recent first
    });
  }, [sessions]);

  // Filter sessions - memoized for performance
  const filteredSessions = useMemo(() => {
    return sortedSessions.filter((session) => {
      const clientName = session.clientName || '';
      const sessionTitle = session.session_title || '';
      const searchLower = searchTerm.toLowerCase().trim();
      
      const matchesSearch = searchTerm === '' || 
        clientName.toLowerCase().includes(searchLower) ||
        sessionTitle.toLowerCase().includes(searchLower);
      
      const currentStatus = session.session_status || session.status || '';
      const matchesStatus = statusFilter === "all" || currentStatus === statusFilter;
      
      return matchesSearch && matchesStatus;
    });
  }, [sortedSessions, searchTerm, statusFilter]);

  useEffect(() => {
    const fetchSessions = async () => {
      try {
        setLoading(true);
        const response = await apiService.getSessionsByCaregiver();
        
        console.log("Sessions API response:", response);
        
        // Process the response to get sessions array
        let sessionsData: Session[] = [];
        
        if (Array.isArray(response)) {
          sessionsData = response;
        } else if (response && Array.isArray(response.data)) {
          sessionsData = response.data;
        } else if (response && Array.isArray(response.sessions)) {
          sessionsData = response.sessions;
        } else if (response && typeof response === 'object') {
          const possibleArrays = Object.values(response).find(value => Array.isArray(value));
          if (possibleArrays) {
            sessionsData = possibleArrays as Session[];
          }
        }
        
        console.log("Processed sessions data:", sessionsData);
        
        // Set sessions immediately for fast initial render
        setSessions(sessionsData);
        setLoading(false);
        
        // Then fetch client data in the background
        if (sessionsData.length > 0) {
          fetchClientDataBatch(sessionsData);
        }
        
      } catch (error) {
        console.error("Error fetching sessions:", error);
        toast({
          title: "Error",
          description: "Failed to fetch sessions.",
          variant: "destructive",
        });
        setSessions([]);
        setLoading(false);
      }
    };

    fetchSessions();
  }, [toast, fetchClientDataBatch]);

  const handleSessionClick = (session: Session) => {
    navigate(`/session/${session.session_id}`, {
      state: {
        clientName: session.clientName,
        sessionDate: formatSessionDate(session.session_timestamp)
      }
    });
  };

  const formatSessionDate = (dateString: string) => {
  if (!dateString) return "Unknown date";
  
  try {
    // Clean up the timestamp to ensure proper parsing
    let cleanedDateString = dateString;
    
    // Handle microseconds - truncate to milliseconds if present
    if (cleanedDateString.includes('.')) {
      const dotIndex = cleanedDateString.indexOf('.');
      const beforeDot = cleanedDateString.substring(0, dotIndex);
      const afterDot = cleanedDateString.substring(dotIndex + 1);
      
      // Check if there's timezone info after microseconds
      const timezoneMatch = afterDot.match(/([+-]\d{2}:?\d{2}|Z)$/);
      if (timezoneMatch) {
        // Has timezone - truncate microseconds to milliseconds
        const microseconds = afterDot.replace(timezoneMatch[0], '');
        const milliseconds = microseconds.substring(0, 3);
        cleanedDateString = `${beforeDot}.${milliseconds}${timezoneMatch[0]}`;
      } else {
        // No timezone info - assume UTC and truncate microseconds
        const milliseconds = afterDot.substring(0, 3);
        cleanedDateString = `${beforeDot}.${milliseconds}Z`;
      }
    } else if (!cleanedDateString.match(/([+-]\d{2}:?\d{2}|Z)$/)) {
      // No microseconds and no timezone - assume UTC
      cleanedDateString = `${cleanedDateString}Z`;
    }
    
    // Create date object - this should now parse correctly
    const date = new Date(cleanedDateString);
    
    // Check if date is valid
    if (isNaN(date.getTime())) {
      console.error('Invalid date parsed:', cleanedDateString);
      return "Invalid date";
    }
    
    // Debug: Log the original vs parsed time
    console.log('Original timestamp:', dateString);
    console.log('Cleaned timestamp:', cleanedDateString);
    console.log('Parsed UTC date:', date.toISOString());
    console.log('Local time:', date.toString());
    
    // Format in local timezone
    const options: Intl.DateTimeFormatOptions = {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    };
    
    const formatted = new Intl.DateTimeFormat('en-US', options).format(date);
    console.log('Formatted local time:', formatted);
    
    return formatted;
  } catch (error) {
    console.error('Date formatting error:', error);
    return "Invalid date";
  }
};

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'processing':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'failed':
        return 'bg-red-100 text-red-800 border-red-200';
      case 'pending':
        return 'bg-gray-100 text-gray-800 border-gray-200';
      case 'scheduled':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return '✅';
      case 'processing':
        return '🔄';
      case 'failed':
        return '❌';
      case 'pending':
        return '⏸️';
      case 'scheduled':
        return '📅';
      default:
        return '📋';
    }
  };

  // Component for displaying client name with loading state
  const ClientNameDisplay = ({ session }: { session: Session }) => {
    if (session.clientLoading) {
      return (
        <div className="flex items-center gap-1">
          <div className="animate-pulse bg-gray-200 h-4 w-20 rounded"></div>
        </div>
      );
    }
    return <span className="truncate">{session.clientName || "Loading..."}</span>;
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <div className="p-3 md:p-6 w-full">
        <Navbar />
        <main className="flex-1 overflow-y-auto mt-4 md:mt-6">
          <div className="max-w-6xl mx-auto px-4 sm:px-6">
            <div className="flex justify-between items-center mb-4 md:mb-6">
              <h1 className="text-xl md:text-2xl font-bold text-gray-900">Sessions</h1>
            </div>
            
            {/* Search and filters */}
            <div className="flex mb-4 md:mb-6 gap-3 md:gap-4 flex-col sm:flex-row">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-2.5 h-4 w-4 text-gray-500" />
                <Input
                  placeholder="Search by session name..."
                  className="pl-9 h-10 md:h-auto text-sm md:text-base"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-full sm:w-48 h-10 md:h-auto text-sm md:text-base">
                  <SelectValue placeholder="Filter by status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Statuses</SelectItem>
                  <SelectItem value="completed">Completed</SelectItem>
                  <SelectItem value="processing">Processing</SelectItem>
                  <SelectItem value="scheduled">Scheduled</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="failed">Failed</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            {/* Sessions list */}
            <div className="space-y-3 md:space-y-4">
              {loading ? (
                <div className="flex flex-col items-center justify-center py-8 md:py-12">
                  <div className="animate-spin rounded-full h-10 w-10 md:h-12 md:w-12 border-t-2 border-b-2 border-psybytes-purple mb-4"></div>
                  <p className="text-gray-500 text-sm md:text-base">Loading sessions...</p>
                </div>
              ) : filteredSessions.length > 0 ? (
                filteredSessions.map((session) => (
                  <div
                    key={session.session_id}
                    className="bg-white rounded-lg p-3 md:p-4 shadow-sm border border-gray-200 transition-all hover:shadow-md cursor-pointer"
                    onClick={() => handleSessionClick(session)}
                  >
                    <div className="flex items-start justify-between flex-col sm:flex-row gap-3 sm:gap-4">
                      <div className="flex-1 min-w-0 w-full">
                        <div className="flex items-start gap-2 mb-2 flex-col sm:flex-row sm:items-center">
                          <h3 className="text-base md:text-lg font-semibold text-gray-800 truncate">
                            {session.session_title || "Session"}
                          </h3>
                          <div className="flex-shrink-0">
                            <Badge className={`${getStatusColor(session.session_status || session.status || '')} text-xs`}>
                              {getStatusIcon(session.session_status || session.status || '')} {session.session_status || session.status}
                            </Badge>
                          </div>
                        </div>
                        
                        <div className="flex items-center gap-3 md:gap-4 text-xs md:text-sm text-gray-600 mb-2 flex-col sm:flex-row">
                          <div className="flex items-center gap-1 w-full sm:w-auto">
                            <User className="h-3 w-3 md:h-4 md:w-4 flex-shrink-0" />
                            <ClientNameDisplay session={session} />
                          </div>
                          <div className="flex items-center gap-1 w-full sm:w-auto">
                            <Calendar className="h-3 w-3 md:h-4 md:w-4 flex-shrink-0" />
                            <span className="truncate">{formatSessionDate(session.session_timestamp)}</span>
                          </div>
                        </div>
                        
                        <div className="flex gap-2 flex-wrap">
                          {session.session_type && (
                            <Badge variant="outline" className="text-xs">
                              {session.session_type}
                            </Badge>
                          )}
                          {session.template_type && (
                            <Badge variant="outline" className="text-xs">
                              {session.template_type.toUpperCase()}
                            </Badge>
                          )}
                        </div>
                      </div>
                      
                      <div className="flex items-center w-full sm:w-auto justify-end">
                        <Button variant="ghost" size="sm" className="text-sm">
                          View Details →
                        </Button>
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-center py-8 md:py-12 px-4">
                  <p className="text-gray-500 text-sm md:text-base">
                    {searchTerm || statusFilter !== "all" ? "No sessions found matching your filters" : "No sessions found"}
                  </p>
                </div>
              )}
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};

export default Sessions;